<script lang="ts">
  import type { Role, User, UserClassSummary } from '$lib/api/types';
  import UserClassList from '$lib/components/users/UserClassList.svelte';
  import { deactivateUser, updateRole } from '$lib/api/users';
  import { goto, invalidate } from '$app/navigation';
  import { page } from '$app/state';
  import AddToClassForm from './AddToClassForm.svelte';
  import { toast } from '$lib/stores/toast.svelte';
  import TableMessage from '../TableMessage.svelte';
  import FormModal from '../common/modal/FormModal.svelte';
  import { auth } from '$lib/stores/auth.svelte';

  interface Props {
    user: User;
    route: Role;
    classesPromise?: Promise<UserClassSummary[]>;
  }
  const { user, route, classesPromise }: Props = $props();

  let roleConfirm: Role | null = $state(null);
  let modalTitle: string | null = $state(null);
  let modalBody: string | null = $state(null);
  let modalAction: 'roleConfirm' | 'deactivate' | 'addClass' | null = $state(null);
  let gotodest: string | null = null; // destination of goto (if needed) when the modal closes out
  let invalidateOnModalClose = false;

  function openModalToConfirmDemotionToStudent() {
    modalAction = 'roleConfirm';
    roleConfirm = 'student';
    modalTitle = `Demote User to Student?`;
    modalBody = `Do you want to demote ${user.firstName} ${user.lastName} to Student? They will no longer have access to this administration panel. ${user.role.includes('instructor') ? 'If the user is an instructor of any clinical groups, they will be removed from those groups.' : ''}`;
  }

  function openModalToConfirmRemoveInstructorRole() {
    modalAction = 'roleConfirm';
    roleConfirm = 'admin';
    modalTitle = `Remove Instructor Role?`;
    modalBody = `Do you want to remove the Instructor role from ${user.firstName} ${user.lastName}? They will be able to manage users, courses, and classes. ${user.role.includes('instructor') ? 'If the user is an instructor of any clinical groups, they will be removed from those groups.' : ''}`;
  }

  function openModalToConfirmRemoveAdminRole() {
    modalAction = 'roleConfirm';
    roleConfirm = 'instructor';
    modalTitle = `Remove Admin Role?`;
    modalBody = `Do you want to remove the Admin role from ${user.firstName} ${user.lastName}? They will no longer be able to manage users, courses, and classes. They can still view their classes and students in clinical groups to which they are assigned as the instructor.`;
  }

  function openModalToConfirmAddInstructorRole() {
    modalAction = 'roleConfirm';
    roleConfirm = 'admin,instructor';
    modalTitle = `Add Instructor Role?`;
    modalBody = `Do you want to assign the Instructor role to ${user.firstName} ${user.lastName}? They will still be able to manage all users, courses, and classes, and they can be assigned to clinical groups as an instructor.`;
  }

  function openModalToConfirmAddAdminRole() {
    modalAction = 'roleConfirm';
    roleConfirm = 'admin,instructor';
    modalTitle = `Add Admin Role?`;
    modalBody = `Do you want to assign the Admin role to ${user.firstName} ${user.lastName}? They will be able to manage all users, courses, and classes. They will still have the instructor role and can be assigned to clinical groups as an instructor.`;
  }

  function openModalToConfirmPromotionToInstructorFromStudent() {
    modalAction = 'roleConfirm';
    roleConfirm = 'instructor';
    modalTitle = `Promote Student to Instructor?`;
    modalBody = `Do you want to promote ${user.firstName} ${user.lastName} to Instructor? They will be able to manage users, courses, and classes, and they can be assigned to clinical groups as an instructor. They will be removed from any classes in which they are currently participating as students.`;
  }

  function openModalToConfirmPromotionToAdminFromStudent() {
    modalAction = 'roleConfirm';
    roleConfirm = 'admin';
    modalTitle = `Promote Student to Admin?`;
    modalBody = `Do you want to promote ${user.firstName} ${user.lastName} to Admin? They will be able to manage users, courses, and classes from this administration panel. They will be removed from any classes in which they are currently participating as students.`;
  }

  function openModalToConfirmDeactivate() {
    modalAction = 'deactivate';
    modalTitle = 'Deactivate User?';
    modalBody = `Do you want to deactivate ${user.firstName} ${user.lastName}? They will no longer be able to sign in.`;
  }

  function openModalToAddUserToClass() {
    modalAction = 'addClass';
    modalTitle = 'Choose class and clinical group';
    modalBody =
      user.role === 'student'
        ? 'Choose a class and a clinical group to add the student. Classes in which the student is already added are not listed.'
        : 'Assign this user as an instructor to a clinical group of a class. Only clinical groups that do not have an assigned instructor are shown.';
  }

  function closeModal() {
    roleConfirm = null;
    modalAction = null;
    modalBody = null;
    modalTitle = null;
    if (gotodest) {
      goto(gotodest);
      gotodest = null;
    }
    if (invalidateOnModalClose) {
      invalidate(url => url.pathname.startsWith('/api/users'));
      invalidateOnModalClose = false;
    }
  }

  async function modalFormSubmit() {
    switch (modalAction) {
      case 'roleConfirm':
        await confirmRoleUpdate();
        break;
      case 'deactivate':
        await confirmDeactivateUser();
        break;
      default:
        throw new Error(`Unexpected modal action found on form submit: ${modalAction}`);
    }
    return true;
  }

  async function confirmRoleUpdate() {
    if (!roleConfirm) {
      throw new Error('expected roleConfirm to not be null!');
    }

    const updatedUser = await updateRole(user.id, roleConfirm);
    if (route === 'admin') {
      if (updatedUser.role === 'student') {
        gotodest = `/admin/users/students/${updatedUser.id}`;
      } else if (updatedUser.role === 'instructor') {
        gotodest = `/admin/users/instructors/${updatedUser.id}`;
      } else {
        invalidateOnModalClose = true;
      }
    } else if (route === 'instructor') {
      if (updatedUser.role === 'admin') {
        gotodest = `/admin/users/admins/${updatedUser.id}`;
      } else if (updatedUser.role === 'student') {
        gotodest = `/admin/users/students/${updatedUser.id}`;
      } else {
        invalidateOnModalClose = true;
      }
    } else if (route === 'student') {
      // only possible values coming from student are 'admin' or 'instructor'. 'admin,instructor' not possible from api
      if (updatedUser.role === 'admin') {
        gotodest = `/admin/users/students/${updatedUser.id}`;
      } else {
        gotodest = `/admin/users/instructors/${updatedUser.id}`;
      }
    }

    toast.showToast('success', 'User role updated successfully!');
  }

  async function confirmDeactivateUser() {
    await deactivateUser(user.id);
    const parentUrl = page.url.pathname.substring(0, page.url.pathname.lastIndexOf('/'));
    gotodest = parentUrl;
    toast.showToast('success', 'User deactivated successfully!');
  }
</script>

<div class="d-flex mb-3 align-items-center gap-3">
  <h2 class="mb-0 flex-grow-1">{user.lastName}, {user.firstName}</h2>
  {#if auth.canImpersonate(user.id)}
    <button class="btn btn-secondary" onclick={() => auth.startAuthImpersonation(user.id)}>Impersonate</button>
  {/if}
  <button class="btn btn-secondary" onclick={openModalToConfirmDeactivate}>Deactivate</button>
  {#if user.role === 'student' || user.role.includes('instructor')}
    <button class="btn btn-primary" onclick={openModalToAddUserToClass}>Add to class</button>
  {/if}
</div>
<div class="row">
  <div class="col-12 col-lg-6 mx-auto mb-5">
    <h3 class="subtle-header">Last Name</h3>
    <div class="form-control mb-3">{user.lastName}</div>
    <h3 class="subtle-header">First Name</h3>
    <div class="form-control mb-3">{user.firstName}</div>
    <h3 class="subtle-header">Email</h3>
    <div class="form-control mb-3">{user.email}</div>
    {#if user.studentId}
      <h3 class="subtle-header">Student ID</h3>
      <div class="form-control mb-3">{user.studentId}</div>
    {/if}
    {#if user.employeeId}
      <h3 class="subtle-header">Employee ID</h3>
      <div class="form-control mb-3">{user.employeeId}</div>
    {/if}
    <h3 class="subtle-header">Universal ID</h3>
    <div class="form-control mb-3">{user.universalId}</div>
    <h3 class="subtle-header">Roles</h3>
    <div class="mb-3">
      {#if user.role === 'student'}
        <span class="badge text-bg-secondary">Student</span>
      {:else if user.role === 'instructor'}
        <span class="badge text-bg-secondary">Instructor</span>
      {:else if user.role === 'admin,instructor'}
        <span class="badge text-bg-secondary">Admin</span>
        <span class="badge text-bg-secondary">Instructor</span>
      {:else}
        <span class="badge text-bg-secondary">Admin</span>
      {/if}
    </div>
    <div class="grid-2-cols mb-3">
      {#if user.role === 'student'}
        <button onclick={openModalToConfirmPromotionToAdminFromStudent} class="btn btn-primary">
          Promote to Admin
        </button>
        <button onclick={openModalToConfirmPromotionToInstructorFromStudent} class="btn btn-primary">
          Promote to Instructor
        </button>
      {:else if user.role === 'instructor'}
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmAddAdminRole}>Add Admin role</button>
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmDemotionToStudent}>
          Demote to Student
        </button>
      {:else if user.role === 'admin,instructor'}
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmRemoveAdminRole}>
          Remove Admin role
        </button>
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmRemoveInstructorRole}>
          Remove Instructor role
        </button>
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmDemotionToStudent}>
          Demote to Student
        </button>
      {:else}
        <!-- user.role === 'admin' -->
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmAddInstructorRole}>
          Add Instructor role
        </button>
        <button type="button" class="btn btn-primary" onclick={openModalToConfirmDemotionToStudent}>
          Demote to Student
        </button>
      {/if}
    </div>
    <div>
      <h3>Role descriptions</h3>
      <ul>
        <li>
          <strong>Admin</strong>: Access to manage users, courses, and classes. Cannot be assigned clinical groups
          unless also having the instructor role. Can view reports of all students of all classes and clinical groups.
        </li>
        <li>
          <strong>Instructor</strong>: Can be assigned clinical groups. Can sign in to this dashboard with read-only
          access to classes and students that are in clinical groups of which the user is an instructor. Can view
          reports only of students in clinical gruops of which the user is an instructor. Can be combined with the admin
          role to allow for user, course, and class management while also serving as instructor for clinical groups.
        </li>
        <li>
          <strong>Student</strong>: Can participate in leaderboards and log skills only while they are assigned to a
          clinical group to a class that is currently within the class's start and end dates. Cannot hold a student role
          while also holding admin or instructor role.
        </li>
      </ul>
    </div>
  </div>
  {#if classesPromise}
    <div class="col-12 col-lg-6 mx-auto">
      {#await classesPromise}
        <TableMessage message="Loading classes..." />
      {:then classes}
        <UserClassList {classes} />
      {/await}
    </div>
  {/if}
</div>

<!-- confirm role update modal -->
{#if modalAction === 'addClass'}
  <AddToClassForm
    classesPromise={user.role === 'student' ? classesPromise : undefined}
    filterClinicalGroups={user.role !== 'student'}
    header={modalTitle!}
    onclose={closeModal}
    userId={user.id}
    userRole={user.role}
  />
{:else if modalAction !== null}
  <FormModal
    scrollable={false}
    formId="user_update"
    header={modalTitle!}
    onclose={closeModal}
    onsubmit={modalFormSubmit}
  >
    <p>
      {modalBody}
    </p>
  </FormModal>
{/if}
